import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { FONT_HEAD, FONT_BODY } from './styles'

// Route + admin gate live in App.js (mirrors TeamsAdmin): render is guarded by
// `&& isAdmin`, and a bounce effect kicks non-admins to /library.
//
// READ ONLY. This screen writes nothing, updates nothing, deletes nothing. It
// exists because dealdesk-chat classifies every turn — tier, tier_reason,
// citation_flag — and persists all of it, and until now nothing read it back.
// Elevated and Stop turns were recorded and never seen.
//
// RLS does the access control. All four Deal Desk tables carry an
// "Admins can read all …" SELECT policy alongside the owner policy, so an admin
// session reads other agents' rows with a plain client query — no service-role
// function needed.

// tier_reason is a slug on the row. Mapped explicitly rather than transformed:
// an algorithm would turn a new trigger into confident-looking nonsense, and
// these are the eleven values dealdesk-chat can actually write (index.ts
// 239-249). Anything not here renders as-is, so a reason added later still
// shows something rather than blank.
const TIER_REASON_LABELS = {
  'earnest-money-dispute': 'Earnest money dispute',
  'remedies': 'Remedies or cancellation',
  'creative-financing': 'Creative financing',
  'party-capacity': 'Party capacity',
  'unrepresented-party': 'Unrepresented party',
  'short-sale': 'Short sale',
  'dual-agency': 'Dual agency',
  'custom-language': 'Custom contract language',
  'financing-terms': 'Financing terms',
  'model-fallback': 'Classifier judgement',
  'fallback-failed': 'Classifier unavailable — failed closed',
}
const reasonLabel = (slug) => (slug ? (TIER_REASON_LABELS[slug] || slug) : null)

// Truncation happens on the raw markdown, before rendering. Slicing rendered
// elements would cut a table in half.
const ANSWER_PREVIEW_CHARS = 400

// Display only. dealdesk_cases.title is whatever the agent typed into the name
// field at creation — nothing derives it from the first message — and the field
// accepts a pasted paragraph as readily as an address. A card whose heading is
// the whole question, repeated verbatim under ASKED a moment later, is unusable
// as a list. The stored title is untouched; the case view still shows it in
// full and still lets the agent rename it.
const TITLE_MAX_CHARS = 60
const capTitle = (t) => {
  const s = String(t || '').trim()
  if (s.length <= TITLE_MAX_CHARS) return s
  // Break on the last space inside the cap so a word is not split, unless the
  // first 60 characters contain no space at all.
  const cut = s.slice(0, TITLE_MAX_CHARS)
  const lastSpace = cut.lastIndexOf(' ')
  return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…'
}

const CHIPS = {
  stop:     { background: '#FCEBEB', color: '#791F1F' },
  elevated: { background: '#FAEEDA', color: '#633806' },
  rep:      { background: '#E4EDF7', color: '#123763' },
  formSet:  { background: '#F4F4F2', color: '#666666' },
  peach:    { background: '#FBE8DA', color: '#6B3410' },
  citation: { background: '#FCEBEB', color: '#791F1F' },
}

const chipBase = {
  display: 'inline-flex', alignItems: 'center',
  padding: '3px 9px', borderRadius: '12px',
  fontSize: '11px', fontWeight: 500, fontFamily: FONT_BODY,
  whiteSpace: 'nowrap',
}

function Chip({ tone, children }) {
  return <span style={{ ...chipBase, ...(CHIPS[tone] || CHIPS.formSet) }}>{children}</span>
}

const eyebrow = {
  fontSize: '10px', fontWeight: 500, letterSpacing: '1.5px',
  color: '#999', fontFamily: FONT_HEAD, marginBottom: '6px',
}

const pill = (selected) => ({
  border: `0.5px solid ${selected ? '#147efb' : '#e8e8e8'}`,
  borderRadius: '18px', padding: '5px 12px', fontSize: '12px',
  fontFamily: FONT_BODY, fontWeight: 500, whiteSpace: 'nowrap',
  background: selected ? '#f4f4f2' : 'transparent',
  color: selected ? '#1d2e42' : '#666',
  cursor: 'pointer', transition: 'all .15s',
})

function formatWhen(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

// renderMd is renderCaseMarkdown, passed down rather than imported: it lives in
// App.js and is not exported, and importing it here would close a cycle
// (App -> this screen -> App). TeamsAdmin avoids depending on App the same way.
export default function DealDeskReviewAdmin({ session, profile, showToast, navigateTo, renderMd }) {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])

  // Two-state segment, an independent toggle, and a dropdown. Stop only is the
  // default: it is the tier that says the tool declined to answer.
  const [scope, setScope] = useState('stop')        // 'stop' | 'both'
  const [citationOnly, setCitationOnly] = useState(false)
  const [agentFilter, setAgentFilter] = useState('')
  const [expanded, setExpanded] = useState({})      // message id -> bool

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // 1. Every flagged assistant turn. No date limit.
      const { data: flagged, error: flaggedErr } = await supabase
        .from('dealdesk_case_messages')
        .select('id, case_id, content, tier, tier_reason, retrieval_status, citation_flag, created_at')
        .eq('role', 'assistant')
        .or('tier.in.(Elevated,Stop),citation_flag.not.is.null')
        .order('created_at', { ascending: false })
      if (flaggedErr) {
        console.error('[dealdesk review] flagged load', flaggedErr)
        showToast('Could not load flagged answers: ' + flaggedErr.message)
        setRows([])
        return
      }
      if (!flagged || flagged.length === 0) { setRows([]); return }

      const caseIds = [...new Set(flagged.map(r => r.case_id).filter(Boolean))]

      // 2 + 3 in parallel: the parent cases, and every question in those cases.
      const [casesRes, questionsRes] = await Promise.all([
        supabase.from('dealdesk_cases')
          .select('id, title, form_set, representation, user_id')
          .in('id', caseIds),
        // mode lives on the USER row, not the assistant row — dealdesk-chat
        // writes it at the question insert and omits it from the answer. So the
        // "drafted without a contract" signal has to come from here.
        supabase.from('dealdesk_case_messages')
          .select('case_id, content, mode, created_at')
          .eq('role', 'user')
          .in('case_id', caseIds)
          .order('created_at', { ascending: true }),
      ])
      if (casesRes.error) console.error('[dealdesk review] cases load', casesRes.error)
      if (questionsRes.error) console.error('[dealdesk review] questions load', questionsRes.error)

      const caseMap = {}
      ;(casesRes.data || []).forEach(c => { caseMap[c.id] = c })

      // 4. Names, only for the agents who appear. Same profileMap shape
      //    AdminPage.loadCases uses, narrowed to the ids in play.
      const userIds = [...new Set((casesRes.data || []).map(c => c.user_id).filter(Boolean))]
      let profileMap = {}
      if (userIds.length > 0) {
        const { data: profs, error: profErr } = await supabase
          .from('profiles').select('id, full_name').in('id', userIds)
        if (profErr) console.error('[dealdesk review] profiles load', profErr)
        ;(profs || []).forEach(p => { profileMap[p.id] = p })
      }

      // Questions grouped per case, ascending, so the pairing below is a scan.
      const questionsByCase = {}
      ;(questionsRes.data || []).forEach(q => {
        if (!questionsByCase[q.case_id]) questionsByCase[q.case_id] = []
        questionsByCase[q.case_id].push(q)
      })

      const decorated = flagged.map(r => {
        const c = caseMap[r.case_id] || null
        // The question is the last user row before this answer. Both are
        // inserted in the same request, so the ordering is reliable; two turns
        // landing in the same millisecond could mis-pair, which is accepted.
        const qs = questionsByCase[r.case_id] || []
        let question = null
        for (const q of qs) {
          if (new Date(q.created_at).getTime() <= new Date(r.created_at).getTime()) question = q
          else break
        }
        return {
          ...r,
          caseTitle: c?.title || 'Untitled case',
          formSet: c?.form_set || null,
          representation: c?.representation || null,
          userId: c?.user_id || null,
          agentName: (c && profileMap[c.user_id]?.full_name) || 'Unknown agent',
          question: question?.content || null,
          questionMode: question?.mode || null,
        }
      })
      setRows(decorated)
    } finally {
      setLoading(false)
    }
  }, [showToast])

  // Mount only, matching TeamsAdmin: depending on `load` would re-fire whenever
  // App.js's showToast identity changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [])

  // Every agent with at least one flagged row, for the dropdown.
  const agentNames = [...new Set(rows.map(r => r.agentName))].sort((a, b) => a.localeCompare(b))

  const visible = rows.filter(r => {
    if (scope === 'stop' && r.tier !== 'Stop') return false
    if (scope === 'both' && !(r.tier === 'Stop' || r.tier === 'Elevated')) return false
    if (citationOnly && !r.citation_flag) return false
    if (agentFilter && r.agentName !== agentFilter) return false
    return true
  })

  return (
    <div>
      <div style={{ padding: '8px 0 20px' }}>
        <div style={{ fontSize: '14px', color: '#666', fontFamily: FONT_BODY, maxWidth: '640px' }}>
          Every Deal Desk answer the classifier flagged, across all agents. Read only — nothing here changes a case.
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <button onClick={() => setScope('stop')} style={pill(scope === 'stop')}>Stop only</button>
        <button onClick={() => setScope('both')} style={pill(scope === 'both')}>Stop + elevated</button>
        <button onClick={() => setCitationOnly(v => !v)} style={pill(citationOnly)}>Citation flagged</button>
        <select
          value={agentFilter}
          onChange={e => setAgentFilter(e.target.value)}
          style={{ ...pill(!!agentFilter), appearance: 'auto', paddingRight: '8px' }}>
          <option value="">All agents</option>
          {agentNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#999', fontFamily: FONT_BODY }}>
          {loading ? 'Loading…' : `${visible.length} of ${rows.length}`}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '40px 0', color: '#666', fontFamily: FONT_BODY }}>Loading flagged answers…</div>
      ) : visible.length === 0 ? (
        <div style={{ padding: '40px 0', color: '#999', fontSize: '14px', fontFamily: FONT_BODY }}>
          No flagged answers yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {visible.map(r => (
            <ReviewCard
              key={r.id}
              row={r}
              renderMd={renderMd}
              expanded={!!expanded[r.id]}
              onToggle={() => setExpanded(e => ({ ...e, [r.id]: !e[r.id] }))}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ReviewCard({ row, renderMd, expanded, onToggle }) {
  const isStop = row.tier === 'Stop'
  const answer = row.content || ''
  const tooLong = answer.length > ANSWER_PREVIEW_CHARS
  const shown = expanded || !tooLong ? answer : answer.slice(0, ANSWER_PREVIEW_CHARS).trimEnd() + '…'
  const reason = reasonLabel(row.tier_reason)

  return (
    <div style={{ background: 'white', border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '16px 18px', fontFamily: FONT_BODY }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
        {row.tier && <Chip tone={isStop ? 'stop' : 'elevated'}>{row.tier}</Chip>}
        {row.representation && (
          <Chip tone="rep">{row.representation === 'seller' ? 'Seller' : 'Buyer'}</Chip>
        )}
        {row.formSet && <Chip tone="formSet">{row.formSet}</Chip>}
        {/* Two different gaps, deliberately distinct. 'unavailable' means the law
            library could not be reached — 'empty' means it answered and nothing
            matched, which is a result rather than a fault and is not flagged. */}
        {row.retrieval_status === 'unavailable' && <Chip tone="peach">Law library down</Chip>}
        {row.questionMode === 'cleanup' && <Chip tone="peach">No contract uploaded</Chip>}
        {row.citation_flag && <Chip tone="citation">Citation flagged</Chip>}
        <span style={{ marginLeft: 'auto', fontSize: '11.5px', color: '#999', whiteSpace: 'nowrap' }}>
          {formatWhen(row.created_at)}
        </span>
      </div>

      <div style={{ fontSize: '13.5px', fontWeight: 500, color: '#1d2e42' }}>
        {/* title carries the full text so the whole thing is still reachable on
            hover, and the ellipsis is not the only record of it. */}
        {row.agentName} · <span title={row.caseTitle}>{capTitle(row.caseTitle)}</span>
      </div>
      {reason && (
        <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>
          Flagged: {reason}
        </div>
      )}

      <div style={{ height: '0.5px', background: '#e8e8e8', margin: '14px 0' }} />

      <div style={eyebrow}>ASKED</div>
      {/* renderCaseMarkdown emits block elements. No whiteSpace pre-wrap: on top
          of blocks it doubles every gap. */}
      <div style={{ fontSize: '14px', lineHeight: 1.65, color: '#1d2e42', overflowWrap: 'anywhere' }}>
        {row.question ? renderMd(row.question) : <span style={{ color: '#999' }}>Question not found</span>}
      </div>

      <div style={{ ...eyebrow, marginTop: '16px' }}>DEAL DESK ANSWERED</div>
      <div style={{ fontSize: '14px', lineHeight: 1.65, color: '#1d2e42', overflowWrap: 'anywhere' }}>
        {renderMd(shown)}
      </div>

      {/* Only in the expanded view. Reviewing a citation flag means seeing which
          reference the guard caught, not just that one fired. */}
      {expanded && row.citation_flag && (
        <div style={{ marginTop: '14px' }}>
          <div style={eyebrow}>UNSOURCED REFERENCES</div>
          <div style={{ fontSize: '12.5px', lineHeight: 1.55, color: '#666', overflowWrap: 'anywhere' }}>
            {row.citation_flag}
          </div>
        </div>
      )}

      {tooLong && (
        <button
          onClick={onToggle}
          style={{ marginTop: '12px', background: 'none', border: 'none', padding: 0, color: '#147efb', fontSize: '12px', fontFamily: FONT_BODY, cursor: 'pointer' }}>
          {expanded ? 'Collapse' : 'Read the full exchange'}
        </button>
      )}
    </div>
  )
}
